import Vue from 'vue';

import { CredentialsTypes, UserCredentialsStatus } from '@ply-io/ply-front-shared-utilities';

import AppActionService from '../AppActionService';
import AppService from '../AppService';
import ElementService from '../element-service';
import { flattenPreserveArrays, isArrayOfObjects, isNullOrUndefinedOrEmpty } from '../helpers';
import {
	MAPPED_FIELDS_KEY,
	TEST_RESULT_STATUSES,
	CONFIGURATIONS_FIELDS_KEY,
	OUTPUT_LABEL_NESTED_KEY,
	OUTPUT_LABEL_VALUE_KEY,
} from '../workflow-node.const';
import IconEmpty from '@/components/FlowTreeBlock/assets/icon-empty.svg';
import IconWarning from '@/components/FlowTreeBlock/assets/icon-exclamation-unknown.svg';
import IconSkipped from '@/components/FlowTreeBlock/assets/icon-vi-skipped.svg';
import IconSuccess from '@/components/FlowTreeBlock/assets/icon-vi.svg';
import IconError from '@/components/FlowTreeBlock/assets/icon-x.svg';
import WidgetFormService from '@/components/WidgetForm/WidgetFormService';
import ParamsService from '@/utils/ParamsService';
import WorkflowService from '@/utils/WorkflowService';
import { ACTION_BUTTON_DEFAULT_TEXT } from '@/utils/alerts/alert.const.js';
import CredentialsService from '@/utils/credentials-service';
import { TEMP_NODE_ID_KEY_PREFIX } from '@/utils/workflow-node.const';
import WorkflowToolService from '@/utils/workflow-tool-service';

const BRANCH_ID_KEY = 'branch_id';

// Private
const HIDDEN_FIELDS_KEY_PREFIX = 'PLY__HIDDEN__';
const LABEL_OUTPUT_FIELD_KEY = 'PLY__LABEL';
const HIDDEN_FIELD_GROUP_KEY = 'hiddenFieldKey';
const HIDDEN_OUTPUT_FIELDS_REGEX = new RegExp(`${HIDDEN_FIELDS_KEY_PREFIX}(?<${HIDDEN_FIELD_GROUP_KEY}>(.+)?)`);

/**
 * removes ply output fields
 * for example: testResult === { name: 'some-name', PLY__HIDDEN__ADDRESS: 'ply hidden value address', PLY__HIDDEN__PHONE: '051-2221332' }
 * return value would be { name: 'some-name' }
 * @param {*} testResult
 * @returns filtered test result, without ply hidden fields
 */
const cleanTestResultPlyHiddenFields = (testResult) =>
	Object.keys(testResult).reduce(
		(filteredTestResult, key) => {
			if (HIDDEN_OUTPUT_FIELDS_REGEX.test(key)) {
				delete filteredTestResult[key];
			}
			return filteredTestResult;
		},
		{ ...testResult }
	);

// Here we'll filter out 'garbage' such as pagination / browser actions
export const cleanHiddenFieldsFromNodeOutput = (testResult, node = null) => {
	const cleanedTestResult = { ...testResult };
	if ('browserAction' in cleanedTestResult) {
		delete cleanedTestResult['browserAction'];
		delete cleanedTestResult['payload'];
		cleanedTestResult[TEST_RESULT_STATUSES.SUCCESS] = true;
	}
	if ('pagination' in cleanedTestResult) {
		delete cleanedTestResult['pagination'];
	}

	if (node && isBranchRootNode(node)) {
		delete cleanedTestResult[BRANCH_ID_KEY];
	}
	return cleanTestResultPlyHiddenFields(cleanedTestResult);
};

/**
 * returns object only with ply hidden fields
 * for example: testResult === { name: 'some-name', PLY__HIDDEN__ADDRESS: 'ply hidden value address', PLY__HIDDEN__PHONE: '051-2221332' }
 * return value would be { ADDRESS: 'ply hidden value address', PHONE: '051-2221332' }
 * @param {*} testResult
 * @returns
 */
export const getHiddenFieldsFromTestResult = (testResult) =>
	Object.entries(testResult).reduce(
		(hiddenFieldsTestResult, [key, value]) => ({
			...hiddenFieldsTestResult,
			...(HIDDEN_OUTPUT_FIELDS_REGEX.test(key) ? { [HIDDEN_OUTPUT_FIELDS_REGEX.exec(key).groups[HIDDEN_FIELD_GROUP_KEY]]: value } : null),
		}),
		{}
	);

/**
 *
 * @param {*} node
 * @param {*} getAction - getAction is supposed to be the actions/getAction getter
 * @returns
 */
export const isConsolidateDataNode = (node, getAction) => {
	const action = getAction(node.action);
	return isConsolidateDataNodeAndAction(node, action);
};

export const isConsolidateDataNodeAndAction = (node, action) => isBranches(node) && AppActionService.isActionOfConsolidateData(action);
export const isBranchRootNode = (node, action) => isBranches(node) && AppActionService.isActionOfBranchRoot(action);

export const isBranchCompoundNode = (node, action) => isBranchRootNode(node, action) || isConsolidateDataNodeAndAction(node, action);
export const isLoopCompoundNode = (node, action) => isLoopRoot(node, action) || isLoopEnd(node, action);

export const isCompoundNode = (node, action) => isBranchCompoundNode(node, action) || isLoopCompoundNode(node, action);

export const getConsolidateDataParamName = (node, paramId) =>
	node?.branch_variables?.find((branchVariable) => branchVariable.uuid === paramId)?.name;

/**
 *
 * @param {*} node - the node object
 * @param {*} getNodeByUuidForWorkflowVersion  - the getter
 * @returns the node id calculated from the uuid
 */

export const getNodeIdFromUUID = (uuid, getNodeByUuidForWorkflowVersion, wfv) => getNodeByUuidForWorkflowVersion(uuid, wfv)?.id;

export const getNodeUUIDFromId = (id, getNode) => getNode(id)?.uuid;

export const isNodeOrphan = (node) => node.is_orphan;

export const isWidget = (node) => AppService.isSlugOfWidget(node?.service_slug);
export const isEndRun = (node) => AppService.isSlugOfEndRun(node?.service_slug);
export const isWebhook = (node) => AppService.isSlugOfWebhook(node?.service_slug);
export const isScheduledWebhook = (node) => AppService.isSlugOfScheduledWebhook(node?.service_slug);
export const isSubflow = (node) => AppService.isSlugOfSubflow(node?.service_slug);
export const isEmailTemplates = (node) => AppService.isSlugOfEmailTemplates(node?.service_slug);
export const isBranches = (node) => AppService.isSlugOfBranches(node?.service_slug);
// TODO: this should be deprecated as 'loop_nodes' is created in the View
export const isLoop = (node) => AppService.isSlugOfLoop(node?.service_slug); // TODO -- need to be deprecated
export const isLoopStart = (node, action) => isLoop(node) && AppActionService.isActionOfLoopStart(action); // TODO -- need to be deprecated
export const isLoopRoot = (node, action) => AppService.isSlugOfLoop(node?.service_slug) && AppActionService.isActionOfLoopStart(action);
export const isLoopEnd = (node, action) => AppService.isSlugOfLoop(node?.service_slug) && AppActionService.isActionOfLoopEnd(action);
export const isSendNotification = ({ service_slug }) => AppService.isSlugOfNotification(service_slug);
export const getNodeWidgetId = (node) => node?.params_map?.widget_id;
export const isTestedSuccessfully = (node) => node.test_result_status === TEST_RESULT_STATUSES.SUCCESS;
export const isTestedUnknown = (node) => node.test_result_status === TEST_RESULT_STATUSES.UNKNOWN;
export const isTestedError = (node) => node.test_result_status === TEST_RESULT_STATUSES.ERROR;
export const isTestedSkipped = (node) => node.test_result_status === TEST_RESULT_STATUSES.SKIPPED;

export const getTestIcon = (node, isChosen = false) => {
	if (isChosen && isTestedUnknown(node)) return IconEmpty;
	if (isTestedSuccessfully(node)) return IconSuccess;
	if (isTestedSkipped(node)) return IconSkipped;
	if (isTestedError(node)) return IconError;
	if (isTestedUnknown(node)) return IconWarning;
	return IconEmpty;
};

export const paramsAreProperlyFilledInParamsMap = (params, paramsMap) =>
	params
		.filter((actionParam) => actionParam.required)
		.every((actionParam) => {
			const { key } = actionParam;
			const paramValue = paramsMap[key];
			if (isNullOrUndefinedOrEmpty(paramValue)) {
				return false;
			}
			return true;
		});

const getRootNodeTestResults = (testResult) => {
	let rootNodeTestResults;
	// The following is a hack/fix so we don't have nested mappedFields.mappedFields:
	if (testResult[MAPPED_FIELDS_KEY]) {
		rootNodeTestResults = { ...testResult[MAPPED_FIELDS_KEY] };
	} else {
		rootNodeTestResults = { ...testResult };
	}
	return rootNodeTestResults;
};

const backwardCompatibleMappedFields = (node, testResult, allWorkflows, userGetter, rootNodeGetters, includeTestValue = false) => {
	const rootNodeTestResults = getRootNodeTestResults(testResult);

	let mappedFields = { ...rootNodeTestResults };
	let userConfigFields = {};
	let globalConfigFields = {};

	const { workflow_version: workflowVersionId } = node;
	const getOriginalWorkflowOfVersion = (workflowsList, versionId) =>
		workflowsList.find((workflow) => WorkflowService.isWorkflowVersionOfWorkflow(workflow, versionId));
	const workflowsList = allWorkflows;
	const workflow = getOriginalWorkflowOfVersion(workflowsList, workflowVersionId);
	if (workflow) {
		const user = userGetter;
		const globalMappedFields = WorkflowService.getGlobalMappedFields(workflow, user, true);

		const { element, elementContext, isNativeElement, nativeElement, getConfigurationResponse } = rootNodeGetters;

		const toolGlobalMappedFields = WorkflowToolService.getToolMappedFields(workflow);

		const elementGlobalMappedFields = ElementService.getElementMappedFields(
			element,
			elementContext,
			isNativeElement,
			nativeElement,
			includeTestValue // includeTestValues flag, hack
		);
		mappedFields = { ...rootNodeTestResults, ...globalMappedFields, ...elementGlobalMappedFields, ...toolGlobalMappedFields };

		userConfigFields = WorkflowService.getUserConfigurationFields(workflow.current_draft, getConfigurationResponse);
		globalConfigFields = WorkflowService.getGlobalConfigurationFields(workflow.current_draft, getConfigurationResponse);
	}
	return {
		...mappedFields,
		[MAPPED_FIELDS_KEY]: mappedFields,
		[CONFIGURATIONS_FIELDS_KEY]: { ...userConfigFields, ...globalConfigFields },
	};
};

export const getGlobalWorkflowRunData = (
	workflow,
	userGetter,
	getConfigurationResponse,
	withConfigurations = true,
	backwardComp = false
) => {
	let configurationFields = {};
	let globalParams = {};

	if (workflow) {
		const user = userGetter;
		globalParams = WorkflowService.getGlobalMappedFields(workflow, user, backwardComp);
		if (withConfigurations) {
			const userConfigFields = WorkflowService.getUserConfigurationFields(workflow.current_draft, getConfigurationResponse);
			const generalConfigFields = WorkflowService.getGlobalConfigurationFields(workflow.current_draft, getConfigurationResponse);
			configurationFields = { ...userConfigFields, ...generalConfigFields };
		}
	}

	const globals = { ...globalParams };

	if (withConfigurations) {
		globals[CONFIGURATIONS_FIELDS_KEY] = configurationFields;
	}

	return globals;
};

export const getPreviousNodeData = (
	node,
	testResult,
	allWorkflows,
	userGetter,
	rootNodeGetters,
	includeTestValue = false,
	backwardComp = false,
	isRootNode = false
) => {
	let previousStepData = {};
	if (isRootNode) {
		let contextualData = {};
		const { workflow_version: workflowVersionId } = node;
		const getOriginalWorkflowOfVersion = (workflowsList, versionId) =>
			workflowsList.find((workflow) => WorkflowService.isWorkflowVersionOfWorkflow(workflow, versionId));
		const workflowsList = allWorkflows;
		const workflow = getOriginalWorkflowOfVersion(workflowsList, workflowVersionId);

		let rootNodeTestResults = { ...testResult };
		if (workflow) {
			const { element, elementContext, isNativeElement, nativeElement, getSubflowInputFields } = rootNodeGetters;

			const toolGlobalMappedFields = WorkflowToolService.getToolMappedFields(workflow);

			const elementGlobalMappedFields = ElementService.getElementMappedFields(
				element,
				elementContext,
				isNativeElement,
				nativeElement,
				includeTestValue // includeTestValues flag, hack
			);

			contextualData = {
				...elementGlobalMappedFields,
				...toolGlobalMappedFields,
			};

			rootNodeTestResults = WorkflowService.isOfTypeSubFlow(workflow) ? getSubflowInputFields(workflow.id) : { ...testResult };
		}

		previousStepData = {
			...contextualData,
			...rootNodeTestResults,
		};

		if (backwardComp) {
			previousStepData = {
				...previousStepData,
				...backwardCompatibleMappedFields(node, testResult, allWorkflows, userGetter, rootNodeGetters, includeTestValue),
			};
		}
	} else {
		previousStepData = { ...testResult };
		if (isWidget(node)) {
			// hack, we use params_map_labels to keep the label of the chosen option
			// if we need to use params_map_labels for another use in widget block, create new test_response_labels field
			const testResultLabels = node.params_map_labels;
			for (const key in previousStepData) {
				// const value = response[key]
				const valueLabel = testResultLabels[key];
				if (valueLabel) {
					Vue.set(previousStepData, key, valueLabel);
				}
			}
		}
	}
	return previousStepData;
};

export const getNodeTestResult = (origNode, cleanHiddenFields = true) => {
	const node = Object.assign({}, origNode);
	const testResult = node?.test_result || {};

	return cleanHiddenFields ? cleanHiddenFieldsFromNodeOutput(testResult, origNode) : testResult;
};

export const getNodeTestResultFlatten = (node) => flattenPreserveArrays(getNodeTestResult(node) || {});

export const getNodeRelevantCredsId = (workflowVersion, node, getConfigurationFieldResponse, isConfigurationsOn) => {
	if (node?.credentials) {
		// override credentials -- this is for responses that are locally and currently saved in store only. ex. installation flow.
		return node?.credentials;
	}

	let credsId = node?.credentials_configuration?.credentials;
	if (isConfigurationsOn && CredentialsService.isConnectionFromConfiguration(node?.credentials_configuration?.credentials_type)) {
		const fieldUUID = node.credentials_configuration.source_field_uuid;
		const configurationId =
			node.credentials_configuration.credentials_type === CredentialsTypes.ProfileBased
				? workflowVersion.user_specific_config_options
				: workflowVersion.general_config_options;
		credsId = getConfigurationFieldResponse(configurationId, fieldUUID);
	}
	return credsId;
};

export const extendParamsMapWithParamsFromDynamicMapperIfExists = function (
	node,
	getConfigurationAndInterfaceIdByFieldUUID,
	getConfigurationFieldResponse,
	isConfigurationsOn,
	params_map,
	getConfigurationFieldResponseFromCache
) {
	if (isConfigurationsOn && node.user_schema_mappings_source_field_uuid) {
		const fieldUUID = node.user_schema_mappings_source_field_uuid;
		try {
			const { configurationId } = getConfigurationAndInterfaceIdByFieldUUID(fieldUUID);
			const configResponse =
				node?.overrideParamsMap && getConfigurationFieldResponseFromCache
					? getConfigurationFieldResponseFromCache(fieldUUID)
					: getConfigurationFieldResponse(configurationId, fieldUUID);

			if (configResponse?.params_map) {
				//todo: validate this is working
				const paramsMap = {};
				Object.keys({ ...params_map, ...configResponse.params_map }).forEach((param) => {
					paramsMap[param] = params_map[param] || configResponse.params_map[param];
				});
				return paramsMap; // { ...params_map, ...configResponse.params_map };
			}
		} catch {
			console.log('not ready yet, initializing');
		}
	}
	return params_map;
};

export const checkIfCredentialsAreValidForNode = (
	workflowVersion,
	appInfo,
	node,
	skipAuthAction,
	getProfile,
	getConfigurationFieldResponse,
	isConfigurationsOn
) => {
	const credsId = getNodeRelevantCredsId(workflowVersion, node, getConfigurationFieldResponse, isConfigurationsOn);
	const credentials = getProfile(credsId);
	return (
		appInfo &&
		(!AppService.doesRequiresAuth(appInfo) || skipAuthAction || (!!credsId && credentials?.status !== UserCredentialsStatus.Staled))
	);
};

export const getTestResponseOutputLabels = (node) => node?.test_result_output_labels;
export const getValueOfTestResponseOutputLabel = (outputLabel) => outputLabel?.[OUTPUT_LABEL_VALUE_KEY];
export const getNestedValuesOfTestResponseOutputLabel = (outputLabel) => outputLabel?.[OUTPUT_LABEL_NESTED_KEY];
export const replaceTableValueWithOutputLabels = (tableKey, tableValue, outputLabels) => {
	const tableOutputLabels = getNestedValuesOfTestResponseOutputLabel(outputLabels?.[tableKey]) || {};
	const replaceValue = ([k, v]) => [k in tableOutputLabels ? getValueOfTestResponseOutputLabel(tableOutputLabels[k]) : k, v];
	return tableValue.map((value) => Object.entries(value).map((itemValue) => replaceValue(itemValue)));
};

export const getOutputLabelForKey = (outputLabels, key) => {
	const keys = ParamsService.separateKey(key);
	let currentOutputLabels = outputLabels;
	const [firstKey, ...restKeys] = keys;
	let currentKey = firstKey;
	if (outputLabels && firstKey in outputLabels) {
		const outputLabelValue = getValueOfTestResponseOutputLabel(currentOutputLabels[firstKey]);
		if (!isNullOrUndefinedOrEmpty(outputLabelValue)) {
			currentKey = getValueOfTestResponseOutputLabel(currentOutputLabels[firstKey]);
		}
		currentOutputLabels = getNestedValuesOfTestResponseOutputLabel(currentOutputLabels[firstKey]);
	}
	if (restKeys.length === 0) {
		return currentKey;
	}
	return ParamsService.joinKeys([currentKey, getOutputLabelForKey(currentOutputLabels, ParamsService.joinKeys(restKeys))]);
};

export const replaceKeysWithOutputLabels = (output, outputLabels) =>
	Object.fromEntries(
		Object.entries(output).map(([key, value]) => {
			let displayKey = key;
			if (key in (outputLabels || {}) && !isArrayOfObjects(value)) {
				displayKey = getValueOfTestResponseOutputLabel(outputLabels[key]);
			}
			return [displayKey, value];
		})
	);

export const isNodeTemp = (node) => node.id?.toString().includes(TEMP_NODE_ID_KEY_PREFIX);

const translatePreviousStepsValue = (value, flatPreviousSteps) =>
	flatPreviousSteps ? WidgetFormService.processPreviousSteps(value, flatPreviousSteps) : value;
export const getNotificationTranslatedProps = (notification, flatPreviousSteps) => ({
	title: translatePreviousStepsValue(notification.text, flatPreviousSteps),
	additionalInfo: translatePreviousStepsValue(notification.extra_text, flatPreviousSteps),
	takeActionTitle: translatePreviousStepsValue(notification.call_to_action, flatPreviousSteps) || ACTION_BUTTON_DEFAULT_TEXT,
});

export const getLabelHiddenFieldFromTestResult = (testResult, itemName) =>
	getHiddenFieldsFromTestResult(testResult)[LABEL_OUTPUT_FIELD_KEY] ||
	`${itemName} label is missing. Re-test step in order to retrieve label.`;
